import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';

import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { NavbarModule } from '@/common/navbar/navbar.module';
import { VindicationApiService } from '@/vindication/vindication.api.service';
import { MessageTemplate } from '@/common/message-generate/message-generate.model';
import { ContractorMailTemplateResponse, TableContractor } from '@/vindication/vindication.model';

import { ContractorInfoComponent } from './contractor-info/contractor-info.component';
import { ContractorTemplatesComponent } from './contractor-templates/contractor-templates.component';

@Component({
	selector: 'app-contractor-edit',
	standalone: true,
	imports: [
		CommonModule,
		MatTabsModule,
		MatProgressSpinner,
		NavbarModule,
		ContractorInfoComponent,
		ContractorTemplatesComponent,
	],
	templateUrl: './contractor-edit.component.html',
	styleUrls: ['./contractor-edit.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorEditComponent implements OnInit {
	readonly contractor = signal<TableContractor | null>(null);

	readonly templates = signal<MessageTemplate[]>([]);
	readonly contractorTemplates = signal<ContractorMailTemplateResponse[]>([]);

	readonly isLoading = signal(false);

	private readonly vindicationApiService = inject(VindicationApiService);
	private readonly route = inject(ActivatedRoute);

	ngOnInit(): void {
		const contractorId = Number(this.route.snapshot.paramMap.get('id'));
		if (!Number.isFinite(contractorId)) {
			return;
		}

		this.isLoading.set(true);

		forkJoin({
			contractor: this.vindicationApiService.getContractor(contractorId),
			templates: this.vindicationApiService.getMailTemplateList(),
			contractorTemplates: this.vindicationApiService.getContractorMailTemplates(contractorId),
		})
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe(({ contractor, templates, contractorTemplates }) => {
				this.contractor.set({
					...contractor,
					phoneLoader: false,
					mailLoader: false,
					noteLoader: false,
					internalStatusLoader: false,
				});
				this.templates.set(templates);
				this.contractorTemplates.set(contractorTemplates);
			});
	}

	patchContractor(patch: Partial<TableContractor>): void {
		const current = this.contractor();
		if (!current) {
			return;
		}
		this.contractor.set({ ...current, ...patch });
	}
}
